import { Context } from '@BlackbirdHQ/ui-base';
import { ListItemText, MenuItem } from '@mui/material';
import * as Schema from 'generated/graphql/schema';
import { camelCase, startCase } from 'lodash';
import * as React from 'react';

import InputValidator, { SelectValidator } from '@/components/validators-forms/input-validator';
import * as Types from '@/types';
import WrapRequiredFieldsDescriptionComponent from '@/views/common-components/wrap-required-fileds-description';

import { TabPanelComponent, TabPropsType } from '.';

export const TabProps: TabPropsType = {
  isValid: (state) =>
    state.name.length > 0 && (state.peripheralType !== 'SENSOR' || state?.config?.sensor?.type !== undefined),
  label: (t) => t('device:basicInformation', { defaultValue: 'Basic information' }),
};

export const TabPanel: TabPanelComponent<{ enumTypes: Types.EnumTypes }> = ({
  handleChange,
  enumTypes,
  state,
  invalidFormMessage,
  classes,
  t,
}) => {
  const onNameChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
    handleChange({}, { name: e.target.value });

  const onDescChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    handleChange({}, { description: e.target.value });
  };

  const { spawn: spawnSnackbar } = React.useContext(Context.Snackbar);

  const onTypeChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const type = e.target.value as Schema.SensorType;
    const newState: { sensor: { type: Schema.SensorType; energyMeter?: boolean } } = {
      sensor: { type },
    };
    // The only sensor types that support energyMeter is Counter and CounterAccumulate, so if energy meter is enabled and we switch away from that type, we disable energyMeter since the toggle will disapear.
    if (
      state.config?.sensor?.energyMeter &&
      type !== Schema.SensorType.COUNTER &&
      type !== Schema.SensorType.COUNTER_ACCUMULATE
    ) {
      newState.sensor.energyMeter = false;
      spawnSnackbar(
        t('shared:energyMeterWasDisabled', {
          defaultValue:
            "Energy Meter has been disabled since it is only supported for 'Counter' and 'Counter Accumulate' sensor types.",
        }),
        'info',
      );
    }
    handleChange(newState);
  };

  const sensorTypes = enumTypes?.sensorTypes ?? [];

  return (
    <div className={classes.tabPanel}>
      <InputValidator
        className={classes.textFields}
        containerClass={classes.containerClass}
        name="name"
        label={t(['device:sensorName'], { defaultValue: 'Sensor name' })}
        validators={['required', 'isString']}
        errorMessages={[
          t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' }),
          t(['shared:mustBeString'], { defaultValue: 'must be a string' }),
        ]}
        value={state.name}
        onChange={onNameChange}
      />
      <br />
      <InputValidator
        className={classes.textFields}
        containerClass={classes.containerClass}
        name="description"
        label={<div>{t(['device:sensorDescription'], { defaultValue: 'Sensor description' })}</div>}
        validators={['isString']}
        errorMessages={[t(['shared:mustBeString'], { defaultValue: 'must be a string' })]}
        value={state.description}
        onChange={onDescChange}
      />
      {state.peripheralType === Schema.PeripheralType.SENSOR ? (
        <>
          <br />
          <SelectValidator
            className={classes.selectFields}
            containerClass={classes.containerClass}
            name="type"
            variant="filled"
            label={`${t(['device:sensorType'], { defaultValue: 'Sensor type' })} *`}
            value={(state?.config?.sensor?.type ?? undefined) || ''}
            validators={['required']}
            errorMessages={[t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' })]}
            onChange={onTypeChange}
          >
            <MenuItem style={{ height: 'auto' }} key="disabled" value={''} disabled>
              {t(['device:selectSensorType'], { defaultValue: 'Select sensor type' })}
            </MenuItem>
            {sensorTypes.map((sensorType) => (
              <MenuItem style={{ height: 'auto' }} key={sensorType.name} value={sensorType.name}>
                <ListItemText
                  primary={startCase(sensorType.name.toLowerCase())}
                  secondary={t([`device:${camelCase(`sensorType${sensorType.name.toLowerCase()}Desc`)}`], {
                    defaultValue: sensorType.description,
                  })}
                />
              </MenuItem>
            ))}
          </SelectValidator>
        </>
      ) : null}
      {invalidFormMessage}
      <br />
      <WrapRequiredFieldsDescriptionComponent />
    </div>
  );
};
